/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Snack']
const MEAL_COLORS = { Breakfast: '#ff9f0a', Lunch: '#30d158', Dinner: '#0a84ff', Snack: '#ff375f' }

const FAMILY_MEMBERS = [
  { email: 'kkdbailey@gmail.com', name: 'Kevin', color: '#e94560' },
  { email: 'moniquebailey1026@gmail.com', name: 'Monique', color: '#5856d6' },
  { email: 'natashabbhatt@gmail.com', name: 'Tasha', color: '#ff9f0a' },
  { email: 'karahauswirth@gmail.com', name: 'Kara', color: '#30d158' },
  { email: 'perniel.grace19@gmail.com', name: 'Grace', color: '#64d2ff' },
  { email: 'mtraetin@gmail.com', name: 'Traetin', color: '#bf5af2' },
  { email: 'ravibhatt150@gmail.com', name: 'Ravi', color: '#ff6b6b' },
]

function getMember(email) {
  const lower = email?.toLowerCase()
  return FAMILY_MEMBERS.find(m => m.email.toLowerCase() === lower) || { name: email?.split('@')[0] || 'Unknown', color: '#8e8e93' }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function ProgressRing({ value, goal, color, size = 120, strokeWidth = 10 }) {
  const pct = goal ? Math.min(100, (value / goal) * 100) : 0
  const r = (size - strokeWidth) / 2
  const circ = 2 * Math.PI * r
  const offset = circ - (pct / 100) * circ
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border-light)" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  )
}

function ActivityCard({ icon, label, value, goal, unit, color, onSave, saving }) {
  const [input, setInput] = useState(String(value || ''))
  const pct = goal ? Math.min(100, Math.round(((value || 0) / goal) * 100)) : 0
  const remaining = Math.max(0, (goal || 0) - (value || 0))

  useEffect(() => { setInput(String(value || '')) }, [value])

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px var(--shadow)', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <p style={{ fontSize: '13px', fontWeight: '700', color: color, marginBottom: '2px' }}>{icon} {label}</p>
          <p style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text-primary)', lineHeight: 1 }}>
            {(value || 0).toLocaleString()}
            <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-tertiary)', marginLeft: '4px' }}>{unit}</span>
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            {remaining.toLocaleString()} {unit} to go · {pct}%
          </p>
        </div>
        <ProgressRing value={value || 0} goal={goal} color={color} size={80} strokeWidth={8} />
      </div>
      <div style={{ height: '4px', background: 'var(--bg-tertiary)', borderRadius: '2px', marginBottom: '16px' }}>
        <div style={{ height: '4px', background: color, borderRadius: '2px', width: `${pct}%`, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          type="number"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="0"
          style={{ flex: 1, padding: '12px 16px', border: '1.5px solid var(--border)', borderRadius: '12px', fontSize: '18px', fontWeight: '700', textAlign: 'center', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none' }}
        />
        <button
          onClick={() => onSave(parseFloat(input) || 0)}
          disabled={saving}
          style={{ padding: '12px 24px', background: color, color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}
        >
          {saving ? '...' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function MemberProgressCard({ log, isMobile }) {
  const member = getMember(log.user_email)
  const items = [
    { label: 'Calories', val: log.calories_eaten || 0, goal: log.calories_goal || 2000, color: '#ff375f', unit: 'cal' },
    { label: 'Steps', val: log.steps_taken || 0, goal: log.steps_goal || 10000, color: '#30d158', unit: 'steps' },
    { label: 'Water', val: log.water_oz || 0, goal: 64, color: '#0a84ff', unit: 'oz' },
  ]
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '16px', boxShadow: '0 1px 4px var(--shadow)', minWidth: isMobile ? '260px' : '280px', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: member.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '15px', flexShrink: 0 }}>
          {member.name.charAt(0)}
        </div>
        <p style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>{member.name}</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
        {items.map(item => {
          const pct = item.goal ? Math.min(100, Math.round((item.val / item.goal) * 100)) : 0
          return (
            <div key={item.label} style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: '4px' }}>
                <ProgressRing value={item.val} goal={item.goal} color={item.color} size={56} strokeWidth={5} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <p style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-primary)' }}>{pct}%</p>
                </div>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{item.label}</p>
              <p style={{ fontSize: '11px', fontWeight: '600', color: item.color }}>{item.val.toLocaleString()}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DailyLog() {
  const isMobile = useIsMobile()
  const [activeTab, setActiveTab] = useState('today')
  const [today, setToday] = useState(new Date().toISOString().split('T')[0])
  const [meals, setMeals] = useState([])
  const [dailyLog, setDailyLog] = useState(null)
  const [familyLogs, setFamilyLogs] = useState([])
  const [userEmail, setUserEmail] = useState('local')
  const [saving, setSaving] = useState(false)
  const [savingSteps, setSavingSteps] = useState(false)
  const [savingWater, setSavingWater] = useState(false)
  const [showMealForm, setShowMealForm] = useState(false)
  const [mealForm, setMealForm] = useState({ meal_type: 'Breakfast', description: '', calories: '' })
  const [caloriesGoal, setCaloriesGoal] = useState(2000)
  const [stepsGoal, setStepsGoal] = useState(10000)
  const [stepsTaken, setStepsTaken] = useState(0)
  const [waterOz, setWaterOz] = useState(0)

  const [weightLog, setWeightLog] = useState(null)
  const [weightForm, setWeightForm] = useState({ weight: '', notes: '' })
  const [weightHistory, setWeightHistory] = useState([])

  useEffect(() => { getUser() }, [])
  useEffect(() => {
    if (userEmail) {
      fetchMeals()
      fetchDailyLog()
      fetchWeightLog()
      fetchWeightHistory()
    }
  }, [userEmail, today])

  async function getUser() {
    try {
      const cookie = document.cookie.split(';').find(c => c.trim().startsWith('CF_Authorization='))
      if (cookie) {
        const token = cookie.trim().replace('CF_Authorization=', '')
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        if (payload.email) { setUserEmail(payload.email); return }
      }
    } catch {}
    const cached = localStorage.getItem('bf_user_email')
    setUserEmail(cached || 'local')
  }

  async function fetchMeals() {
    const { data, error } = await supabase.from('meal_logs').select('*')
      .eq('date', today).eq('user_email', userEmail).order('created_at', { ascending: true })
    if (!error) setMeals(data || [])
  }

  async function fetchDailyLog() {
    const { data, error } = await supabase.from('daily_logs').select('*')
      .eq('date', today).eq('user_email', userEmail).single()
    if (!error && data) {
      setDailyLog(data)
      setCaloriesGoal(data.calories_goal || 2000)
      setStepsGoal(data.steps_goal || 10000)
      setStepsTaken(data.steps_taken || 0)
      setWaterOz(data.water_oz || 0)
    } else {
      setDailyLog(null)
      setCaloriesGoal(2000); setStepsGoal(10000); setStepsTaken(0); setWaterOz(0)
    }

    // Fetch all other family members who logged today
    const { data: others } = await supabase.from('daily_logs').select('*')
      .eq('date', today).neq('user_email', userEmail)
    setFamilyLogs(others || [])
  }

  async function fetchWeightLog() {
    const { data, error } = await supabase.from('weight_logs').select('*')
      .eq('date', today).eq('user_email', userEmail).single()
    if (!error && data) { setWeightLog(data); setWeightForm({ weight: data.weight || '', notes: data.notes || '' }) }
    else { setWeightLog(null); setWeightForm({ weight: '', notes: '' }) }
  }

  async function fetchWeightHistory() {
    const { data, error } = await supabase.from('weight_logs').select('*')
      .eq('user_email', userEmail).order('date', { ascending: true }).limit(60)
    if (!error && data) {
      setWeightHistory(data.map(d => ({
        date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: d.weight
      })))
    }
  }

  async function saveDailyLogField(field, value) {
    const payload = {
      calories_goal: caloriesGoal,
      steps_goal: stepsGoal,
      steps_taken: stepsTaken,
      water_oz: waterOz,
      calories_eaten: totalCalories,
      date: today,
      user_email: userEmail,
      [field]: value
    }
    if (dailyLog) {
      await supabase.from('daily_logs').update(payload).eq('id', dailyLog.id)
    } else {
      await supabase.from('daily_logs').insert([payload])
    }
    fetchDailyLog()
  }

  async function saveSteps(val) {
    setSavingSteps(true); setStepsTaken(val)
    await saveDailyLogField('steps_taken', val)
    setSavingSteps(false)
  }

  async function saveWater(val) {
    setSavingWater(true); setWaterOz(val)
    await saveDailyLogField('water_oz', val)
    setSavingWater(false)
  }

  async function saveWeightLog() {
    if (!weightForm.weight) return
    setSaving(true)
    const payload = { date: today, user_email: userEmail, weight: parseFloat(weightForm.weight), notes: weightForm.notes }
    if (weightLog) { await supabase.from('weight_logs').update(payload).eq('id', weightLog.id) }
    else { await supabase.from('weight_logs').insert([payload]) }
    setSaving(false)
    fetchWeightLog(); fetchWeightHistory()
  }

  async function addMeal() {
    if (!mealForm.description || !mealForm.calories) return
    setSaving(true)
    await supabase.from('meal_logs').insert([{
      date: today, user_email: userEmail,
      meal_type: mealForm.meal_type, description: mealForm.description,
      calories: parseInt(mealForm.calories) || 0
    }])
    setMealForm({ meal_type: 'Breakfast', description: '', calories: '' })
    setShowMealForm(false); setSaving(false); fetchMeals()
    await saveDailyLogField('calories_eaten', totalCalories + (parseInt(mealForm.calories) || 0))
  }

  async function deleteMeal(id) {
    await supabase.from('meal_logs').delete().eq('id', id)
    fetchMeals()
  }

  const totalCalories = meals.reduce((sum, m) => sum + (m.calories || 0), 0)
  const mealsByType = MEAL_TYPES.reduce((acc, type) => {
    acc[type] = meals.filter(m => m.meal_type === type); return acc
  }, {})
  const latestWeight = weightHistory.length > 0 ? weightHistory[weightHistory.length - 1].weight : null
  const firstWeight = weightHistory.length > 1 ? weightHistory[0].weight : null
  const weightChange = latestWeight && firstWeight ? (latestWeight - firstWeight).toFixed(1) : null
  const calPct = caloriesGoal ? Math.min(100, Math.round((totalCalories / caloriesGoal) * 100)) : 0

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif', maxWidth: '680px', margin: '0 auto' }}>
      <style>{`input[type="number"],input[type="text"],input[type="date"],select,textarea{font-size:16px!important}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.5px', marginBottom: '2px' }}>Daily Log</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>{new Date(today + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <input type="date" value={today} onChange={e => setToday(e.target.value)} style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '14px', color: 'var(--text-primary)', background: 'var(--bg-input)' }} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: 'var(--bg-tertiary)', borderRadius: '12px', padding: '3px', gap: '2px', marginBottom: '20px' }}>
        {[{ id: 'today', label: 'Today' }, { id: 'weight', label: 'Weight' }, { id: 'history', label: 'History' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', background: activeTab === tab.id ? 'var(--bg-card)' : 'transparent', color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-tertiary)', boxShadow: activeTab === tab.id ? '0 1px 3px var(--shadow-md)' : 'none' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* TODAY TAB */}
      {activeTab === 'today' && (
        <>
          {/* Calorie Hero */}
          <div style={{ background: 'linear-gradient(135deg, #ff375f, #ff6b6b)', borderRadius: '20px', padding: '24px', marginBottom: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <ProgressRing value={totalCalories} goal={caloriesGoal} color="rgba(255,255,255,0.9)" size={110} strokeWidth={10} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <p style={{ fontSize: '22px', fontWeight: '800', lineHeight: 1, color: 'white' }}>{calPct}%</p>
                <p style={{ fontSize: '10px', opacity: 0.8, fontWeight: '600' }}>calories</p>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '32px', fontWeight: '800', lineHeight: 1, marginBottom: '4px' }}>{totalCalories.toLocaleString()}</p>
              <p style={{ fontSize: '14px', opacity: 0.85 }}>of {caloriesGoal.toLocaleString()} cal goal</p>
              <p style={{ fontSize: '13px', opacity: 0.7, marginTop: '4px' }}>{Math.max(0, caloriesGoal - totalCalories).toLocaleString()} remaining</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                <p style={{ fontSize: '12px', opacity: 0.8 }}>Goal:</p>
                <input type="number" value={caloriesGoal} onChange={e => setCaloriesGoal(parseInt(e.target.value) || 2000)} onBlur={() => saveDailyLogField('calories_goal', caloriesGoal)} style={{ width: '80px', padding: '4px 8px', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', fontSize: '13px', fontWeight: '600', textAlign: 'center', background: 'rgba(255,255,255,0.2)', color: 'white' }} />
                <p style={{ fontSize: '12px', opacity: 0.8 }}>cal</p>
              </div>
            </div>
          </div>

          {/* Meals */}
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px var(--shadow)', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <p style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>🍽️ Meals</p>
              <button onClick={() => setShowMealForm(!showMealForm)} style={{ padding: '8px 16px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>+ Add</button>
            </div>
            {showMealForm && (
              <div style={{ background: 'var(--bg-tertiary)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr', gap: '8px', marginBottom: '10px' }}>
                  <select value={mealForm.meal_type} onChange={e => setMealForm({ ...mealForm, meal_type: e.target.value })} style={{ padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '14px' }}>
                    {MEAL_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <input placeholder="What did you eat?" value={mealForm.description} onChange={e => setMealForm({ ...mealForm, description: e.target.value })} style={{ padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '14px' }} />
                  <input placeholder="Calories" type="number" value={mealForm.calories} onChange={e => setMealForm({ ...mealForm, calories: e.target.value })} style={{ padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '14px' }} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={addMeal} disabled={saving} style={{ flex: 1, padding: '10px', background: '#ff375f', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Save</button>
                  <button onClick={() => setShowMealForm(false)} style={{ padding: '10px 16px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
                </div>
              </div>
            )}
            {MEAL_TYPES.map(type => (
              mealsByType[type].length > 0 && (
                <div key={type} style={{ marginBottom: '14px' }}>
                  <p style={{ fontSize: '12px', fontWeight: '700', color: MEAL_COLORS[type], marginBottom: '8px', letterSpacing: '0.5px' }}>{type.toUpperCase()}</p>
                  {mealsByType[type].map(meal => (
                    <div key={meal.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-tertiary)', borderRadius: '10px', marginBottom: '6px' }}>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)', flex: 1 }}>{meal.description}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <p style={{ fontSize: '14px', fontWeight: '700', color: MEAL_COLORS[type] }}>{meal.calories} cal</p>
                        <button onClick={() => deleteMeal(meal.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'var(--text-hint)' }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ))}
            {meals.length === 0 && !showMealForm && (
              <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', textAlign: 'center', padding: '16px 0' }}>No meals logged yet.</p>
            )}
          </div>

          {/* Steps */}
          <ActivityCard icon="👟" label="Steps" value={stepsTaken} goal={stepsGoal} unit="steps" color="#30d158" onSave={saveSteps} saving={savingSteps} />

          {/* Water */}
          <ActivityCard icon="💧" label="Water" value={waterOz} goal={64} unit="oz" color="#0a84ff" onSave={saveWater} saving={savingWater} />

          {/* Family progress */}
          {familyLogs.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <p style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.5px', marginBottom: '12px' }}>TODAY'S FAMILY PROGRESS</p>
              <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px', WebkitOverflowScrolling: 'touch' }}>
                {familyLogs.map(log => (
                  <MemberProgressCard key={log.user_email} log={log} isMobile={isMobile} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* WEIGHT TAB */}
      {activeTab === 'weight' && (
        <>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 4px var(--shadow)', marginBottom: '12px' }}>
            <p style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.5px', marginBottom: '16px' }}>LOG WEIGHT</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Weight (lbs)</p>
                <input type="number" step="0.1" value={weightForm.weight} placeholder="0.0" onChange={e => setWeightForm({ ...weightForm, weight: e.target.value })} style={{ width: '100%', padding: '12px', border: '1.5px solid var(--border)', borderRadius: '12px', fontSize: '28px', fontWeight: '800', textAlign: 'center', color: '#ff375f', background: 'var(--bg-input)' }} />
              </div>
              <div>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Notes (optional)</p>
                <input type="text" value={weightForm.notes} placeholder="e.g. morning weigh-in" onChange={e => setWeightForm({ ...weightForm, notes: e.target.value })} style={{ width: '100%', padding: '12px', border: '1.5px solid var(--border)', borderRadius: '12px', fontSize: '14px', background: 'var(--bg-input)', color: 'var(--text-primary)' }} />
              </div>
            </div>
            <button onClick={saveWeightLog} disabled={saving || !weightForm.weight} style={{ width: '100%', padding: '14px', background: weightForm.weight ? '#ff375f' : 'var(--bg-tertiary)', color: weightForm.weight ? 'white' : 'var(--text-hint)', border: 'none', borderRadius: '12px', cursor: weightForm.weight ? 'pointer' : 'default', fontSize: '15px', fontWeight: '600' }}>
              {saving ? 'Saving...' : weightLog ? 'Update Weight' : 'Save Weight'}
            </button>
          </div>
          {weightHistory.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
              {[
                { label: 'Current', val: `${latestWeight} lbs`, color: '#ff375f' },
                { label: 'Change', val: weightChange !== null ? `${parseFloat(weightChange) > 0 ? '+' : ''}${weightChange} lbs` : '—', color: parseFloat(weightChange) <= 0 ? '#30d158' : '#ff375f' },
                { label: 'Logged', val: `${weightHistory.length} days`, color: 'var(--text-primary)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--bg-card)', borderRadius: '14px', padding: '16px', textAlign: 'center', boxShadow: '0 1px 4px var(--shadow)' }}>
                  <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>{s.label}</p>
                  <p style={{ fontSize: '18px', fontWeight: '700', color: s.color }}>{s.val}</p>
                </div>
              ))}
            </div>
          )}
          {weightHistory.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: 'var(--bg-card)', borderRadius: '16px', boxShadow: '0 1px 4px var(--shadow)' }}>
              <p style={{ fontSize: '40px', marginBottom: '12px' }}>⚖️</p>
              <p style={{ fontSize: '15px', color: 'var(--text-tertiary)' }}>Log your weight above to start tracking your trend.</p>
            </div>
          )}
        </>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <>
          {weightHistory.length > 1 ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 4px var(--shadow)', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.5px', marginBottom: '20px' }}>WEIGHT TREND</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={weightHistory} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickLine={false} axisLine={false} domain={['dataMin - 2', 'dataMax + 2']} />
                  <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text-primary)' }} />
                  <Line type="monotone" dataKey="weight" stroke="#ff375f" strokeWidth={2.5} dot={{ fill: '#ff375f', r: 4 }} activeDot={{ r: 6 }} name="Weight (lbs)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: 'var(--bg-card)', borderRadius: '16px', boxShadow: '0 1px 4px var(--shadow)' }}>
              <p style={{ fontSize: '40px', marginBottom: '12px' }}>📈</p>
              <p style={{ fontSize: '15px', color: 'var(--text-tertiary)' }}>Log weight for at least 2 days to see your trend.</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default DailyLog
